import { AppBarData } from "@/appBar/appBarDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { isReactElement } from "@/utils/reactHelpers";
import { forwardRef, ReactElement, ReactNode, useRef } from "react";
import { AlertContainer } from "./Alert";
import { AppBar } from "./AppBar";
import { Box, VBox, VBoxProps } from "./Box";
import { BackButton, Button, CloseButton } from "./Button";
import { BgColor } from "./colors";
import { DropdownMenu, DropdownMenuProps } from "./Dropdown";
import { useTranslation } from "@/stores/RootStoreContext";
import { BoxProps } from "./boxHelpers";
import { LoadingIndicator } from "./LoadingIndicator";
import { DCEvent } from "@/events/eventsDomain";

export type PageHeaderProps = {
  title: ReactElement | string | number;
  menu?: DropdownMenuProps | ReactElement;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onGoBack?: (() => void) | DCEvent[];
  onGoBackName?: string;
  onGoBackVariant?: "arrow" | "x";
  containerProps?: BoxProps;
};

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(function PageHeader(
  {
    title,
    menu,
    isMaximized,
    onToggleMaximize,
    onGoBack,
    onGoBackVariant,
    onGoBackName,
    containerProps,
  },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box
      ref={ref}
      paddingX
      paddingY={"sm"}
      borderBottom
      bg
      gap={"lg"}
      alignItems={"center"}
      {...containerProps}
    >
      {onGoBack &&
        (onGoBackVariant === "x" ? (
          <CloseButton onClick={onGoBack} />
        ) : (
          <BackButton onClick={onGoBack} name={onGoBackName} />
        ))}
      {title}
      <Box marginLeft={"auto"}>
        {typeof menu === "object" && "button" in menu ? <DropdownMenu {...menu} /> : menu}
      </Box>
      {onToggleMaximize && (
        <Button
          variant={"basic"}
          icon={isMaximized ? "arrows-in-simple" : "arrows-out-simple"}
          tooltip={isMaximized ? t("restore") : t("maximize")}
          onClick={onToggleMaximize}
        />
      )}
    </Box>
  );
});

export type FullScreenLayoutProps = {
  appBar?: AppBarData | ReactElement | null | false;
  alertStore?: AlertMessageStore;
  pageHeader?: PageHeaderProps | ReactElement | false;
  leftPanelContainerProps?: BoxProps;
  leftPanel?: ReactNode;
  centerPanelContainerProps?: BoxProps;
  centerPanel?: ReactElement | "loader";
  rightPanelContainerProps?: VBoxProps;
  rightPanel?: ReactElement;
  footerPanel?: ReactElement;
  bg?: BgColor | boolean;
  disableAppBarShadow?: boolean;
  disableAppBarBorder?: boolean;
  className?: string;
};

export const FullScreenLayout = forwardRef<HTMLDivElement, FullScreenLayoutProps>(
  function FullScreenLayout(
    {
      appBar,
      alertStore,
      pageHeader,
      leftPanelContainerProps,
      leftPanel,
      centerPanelContainerProps,
      centerPanel,
      rightPanelContainerProps,
      rightPanel,
      footerPanel,
      bg = "base",
      disableAppBarShadow,
      disableAppBarBorder,
      className,
    },
    ref,
  ) {
    const appBarContainerRef = useRef<HTMLDivElement | null>(null);
    const hasPageHeader = Boolean(pageHeader);
    const hasLeftPanel = Boolean(leftPanel);
    const hasRightPanel = Boolean(rightPanel);
    const isMaximized = pageHeader && !isReactElement(pageHeader) && pageHeader.isMaximized;

    return (
      <VBox className={className} ref={ref} fullscreen gap={0}>
        {appBar && !isMaximized && (
          <VBox ref={appBarContainerRef} flex={"none"} gap={0}>
            {isReactElement(appBar) ? (
              appBar
            ) : (
              <AppBar
                data={appBar}
                disableDropShadow={
                  disableAppBarShadow || hasPageHeader || hasLeftPanel || hasRightPanel
                }
                borderBottom={disableAppBarBorder ? false : undefined}
              />
            )}
          </VBox>
        )}
        {alertStore && (
          <AlertContainer messageStore={alertStore} offset={appBar ? "above" : "below"} />
        )}
        {pageHeader && (
          <VBox flex={"none"} gap={0}>
            {isReactElement(pageHeader) ? pageHeader : <PageHeader {...pageHeader} />}
          </VBox>
        )}
        <Box gap={0} overflow={"hidden"} bg={bg} grow>
          {leftPanel && (
            <Box vertical overflowY={"auto"} borderRight bg {...leftPanelContainerProps}>
              {leftPanel}
            </Box>
          )}
          {centerPanel && (
            <Box vertical overflowY={"auto"} gap={0} grow {...centerPanelContainerProps}>
              {centerPanel === "loader" ? (
                <VBox grow={"golden"} justifyContent="center">
                  <LoadingIndicator />
                </VBox>
              ) : (
                centerPanel
              )}
            </Box>
          )}
          {rightPanel && (
            <VBox overflowY={"auto"} borderLeft gap={0} bg {...rightPanelContainerProps}>
              {rightPanel}
            </VBox>
          )}
        </Box>
        {footerPanel && (
          <VBox flex={"none"} gap={0}>
            {footerPanel}
          </VBox>
        )}
      </VBox>
    );
  },
);
